// Qué tiene que mostrar la escena 3D, derivado del estado del motor
// (T-001-06).
//
// Este módulo es puro: no importa React ni Three. Vive fuera de
// `src/engine` porque es una decisión de presentación —quién está en el
// escenario y qué gesto le toca—, pero se mantiene sin dependencias para
// poder probarlo sin WebGL, que es exactamente lo que jsdom ofrece.
//
// Regla dura de este hito (PLAN-001, T-001-06): solo entran al escenario los
// personajes cuyo GLB está sincronizado, hoy Capi y Tomi. Luna, Clara y Beto
// quedan registrados en `shared/assets.ts` pero no se descargan: cuando hablan,
// lo que se ve es su retrato 2D y el diálogo, que es la experiencia completa.

import type { RuntimeState, RuntimeView } from "../../engine";
import { PRELOADED_CHARACTER_IDS, type CharacterId } from "../../shared/assets";
import { estadoVisualDeReparacionPuente } from "./reparacion-puente";
import { presentacionBrujula, type PresentacionBrujula } from "./presentacion-brujula";

/**
 * Qué está haciendo un personaje. No es un `RuntimeClip`: la traducción a
 * clip real la hace `shared/animation-intents.ts`, que es el único lugar
 * autorizado a decidir qué clip representa qué (PLAN-001: "el mapa de
 * animaciones nunca adivina por nombre en tiempo de ejecución").
 */
export type GestoDeEscena =
  /** Intención escrita en el guion (`anim` del nodo), tal cual. */
  | { readonly tipo: "intencion"; readonly intent: string; readonly evento?: string }
  /**
   * El personaje espera una decisión del niño. No viene del guion: es una
   * decisión de presentación explícita —quien pregunta, escucha— y por eso
   * se modela como un caso propio en vez de inventarse una intención.
   */
  | { readonly tipo: "escuchar" }
  /** Nada que representar: reposo. */
  | { readonly tipo: "reposo" };

export interface EstadoDeEscena {
  /** Presentation-only prop contract; not stored in runtime progress. */
  readonly brujula?: PresentacionBrujula;
  readonly sceneId: string;
  /** Encuadre semántico de la escena/nodo, declarado en el contenido. */
  readonly cameraPreset: string;
  /**
   * Estado del entorno que declara la escena (`bridge_main: "broken"`, etc.).
   * El decorado lo consulta para no contradecir al guion.
   */
  readonly environment: Readonly<Record<string, string>>;
  /** Número de tablas ya visibles durante la reconstrucción simbólica del puente. */
  readonly planchasPuenteVisibles: number;
  /** Personajes en el escenario: solo los que tienen GLB sincronizado. */
  readonly personajes: readonly CharacterId[];
  /** Quién habla o escucha ahora, si tiene GLB. */
  readonly protagonista: CharacterId | null;
  /** Actuación compartida explícita del elenco, sin cambiar posiciones. */
  readonly actuantes?: readonly CharacterId[];
  readonly gesto: GestoDeEscena;
  /**
   * Solo las variables de saludo (`greetCapi`, `greetTomi`, `greetBeto`), que
   * son las que `greet_from_session:*` necesita. El resto de la sesión no
   * llega aquí: `insistedLuna` registra que alguien pasó por encima de un
   * «no», y ningún gesto puede depender de eso ni ahora ni por descuido más
   * adelante (Constitución III; guardarraíl de docs/ESTADO-IMPLEMENTACION.md).
   */
  readonly sessionVars: Readonly<Record<string, string | boolean>>;
}

function tieneModeloSincronizado(castId: string): castId is CharacterId {
  return (PRELOADED_CHARACTER_IDS as readonly string[]).includes(castId);
}

/** Prefijo de las únicas variables de sesión que la escena puede ver. */
const PREFIJO_DE_SALUDO = "greet";

function soloSaludos(
  sessionVars: Readonly<Record<string, string | boolean>>,
): Readonly<Record<string, string | boolean>> {
  return Object.fromEntries(
    Object.entries(sessionVars).filter(([clave]) => clave.startsWith(PREFIJO_DE_SALUDO)),
  );
}

/** Personajes del reparto de la escena que sí se pueden mostrar en 3D. */
function personajesDe(vista: RuntimeView): readonly CharacterId[] {
  return vista.scene.cast.filter(tieneModeloSincronizado);
}

/**
 * Traduce la vista actual del motor a lo que la escena debe representar.
 *
 * - En una línea, habla quien la dice, con la intención del guion.
 * - En una decisión, escucha quien hizo la pregunta (la línea previa de la
 *   escena, que el motor entrega en `precedingLine`).
 * - En el cierre, en un nodo sin interfaz o en un error, nadie actúa: reposo.
 */
export function estadoDeEscenaDesde(
  vista: RuntimeView,
  estado: RuntimeState,
  acuerdosPuenteColocados = 0,
  puenteRestaurado = false,
  choqueVisual: { readonly nodeId: string; readonly secuencia: number } | null = null,
): EstadoDeEscena {
  const event =
    vista.kind === "line"
      ? vista.node.worldEvent
      : vista.kind === "reward"
        ? { restore: vista.node.restore }
        : undefined;
  const reparacionPuente = estadoVisualDeReparacionPuente({
    sceneId: vista.scene.id,
    environment: vista.scene.environment,
    acuerdosColocados: acuerdosPuenteColocados,
    event,
    restauracionConfirmada: puenteRestaurado,
  });
  const base = {
    brujula: presentacionBrujula(vista.scene.id, "node" in vista ? vista.node.id : ""),
    sceneId: vista.scene.id,
    cameraPreset: vista.kind === "line" ? vista.node.camera ?? vista.scene.camera : vista.scene.camera,
    environment: reparacionPuente.environment,
    planchasPuenteVisibles: reparacionPuente.planchasVisibles,
    personajes: personajesDe(vista),
    sessionVars: soloSaludos(estado.sessionVars),
  } as const;

  if (vista.kind === "reward" && vista.node.celebration?.anim === "cross_bridge_celebrate") {
    const actuantes = vista.node.celebration.cast.filter(tieneModeloSincronizado)
      .filter(id => base.personajes.includes(id));
    // Celebración corporal; el desplazamiento coordinado por el puente es
    // una coreografía distinta y no se simula activando Walk en reposo.
    // Durante la actuación importa el elenco, no el primer plano del vano:
    // bridge_wide desplazaba el rig y recortaba a Clara y Capi.
    return { ...base, cameraPreset: "celebracion_elenco", protagonista: null, actuantes,
      gesto: { tipo: "intencion", intent: "celebrate", evento: vista.node.id } };
  }

  if (vista.kind === "minigame" && vista.node.minigameId === "high_five_rhythm" &&
      choqueVisual?.nodeId === vista.node.id && Number.isSafeInteger(choqueVisual.secuencia) && choqueVisual.secuencia > 0 &&
      base.personajes.includes("capi") && base.personajes.includes("tomi")) {
    return { ...base, protagonista: "tomi", gesto: { tipo: "intencion", intent: "greet_high_five", evento: `${vista.node.id}:choque:${choqueVisual.secuencia}` } };
  }

  if (vista.kind === "line") {
    const hablante = tieneModeloSincronizado(vista.speaker) ? vista.speaker : null;
    return {
      ...base,
      protagonista: hablante,
      cameraPreset: vista.node.anim === "jump" ? "salto_narrativo" : base.cameraPreset,
      gesto: hablante ? { tipo: "intencion", intent: vista.node.anim, evento: vista.node.id } : { tipo: "reposo" },
    };
  }

  if (vista.kind === "choice") {
    const pregunta = vista.precedingLine;
    const quienPregunto =
      pregunta && tieneModeloSincronizado(pregunta.speaker) ? pregunta.speaker : null;
    return {
      ...base,
      protagonista: quienPregunto,
      gesto: quienPregunto ? { tipo: "escuchar" } : { tipo: "reposo" },
    };
  }

  return { ...base, protagonista: null, gesto: { tipo: "reposo" } };
}
